import type { Palette } from "./types";

// Curated, clinical-safe palettes for the +CURA Studio brand picker.
//
// Design intent (Phase 3):
//  - Cover the realistic GP / regional health space: NHS nations, care types,
//    independent / private providers, lab vendors, and neutral fallbacks.
//  - Every primary passes WCAG AA on white (>= 4.5:1) — verified against the
//    helpers in ./contrast.ts so the picker won't flag any default as failing.
//  - Tuple shape is [primary, secondary, wash]:
//      * primary   = brand accent (cardboard stripes, lab-form header)
//      * secondary = darker hover/pressed shade (~40% darker by channel)
//      * wash      = pale tint for backgrounds (~8% primary + 92% white)
//  - Ordered by hue group so the picker strip reads visually:
//    blues -> teals -> greens -> purples -> reds/rose -> ambers -> neutrals.
//  - IDs are lowercase kebab-case and stable; renaming an existing id will
//    break any BrandProfile that references it. Add new, don't rename.
export const PALETTES: Palette[] = [
  // Blues — NHS nations, primary care, corporate health
  { id: "nhs",            name: "NHS Blue",                 colors: ["#005EB8", "#00386E", "#EBF2F9"] },
  { id: "nhs-scotland",   name: "NHS Scotland blue",        colors: ["#0065A4", "#003D62", "#EBF3F8"] },
  { id: "nhs-ni",         name: "NHS Northern Ireland",     colors: ["#00558C", "#003354", "#EBF1F6"] },
  { id: "primary-care",   name: "Primary care blue",        colors: ["#1F5AA8", "#133665", "#EDF2F8"] },
  { id: "insurer-steel",  name: "Insurer steel blue",       colors: ["#3A6FB3", "#23436B", "#EFF3F9"] },
  { id: "navy",           name: "Deep Navy",                colors: ["#1F2D5C", "#131B37", "#EDEEF2"] },

  // Teals — HSE Ireland, pharmacy, biotech
  { id: "hse-ireland",    name: "HSE Ireland teal",         colors: ["#00767A", "#004749", "#EBF4F4"] },
  { id: "teal",           name: "Clinic Teal",              colors: ["#00857C", "#00504A", "#E2F1EF"] },
  { id: "pharmacy",       name: "Community pharmacy teal",  colors: ["#0F766E", "#094742", "#ECF4F3"] },
  { id: "biotech",        name: "Bio-tech green",           colors: ["#0F7A5F", "#094939", "#ECF4F2"] },

  // Greens — NHS Wales, diabetic care, general forest
  { id: "nhs-wales",      name: "NHS Wales green",          colors: ["#007A33", "#00491F", "#EBF4EF"] },
  { id: "diabetic",       name: "Diabetic care green",      colors: ["#3F7D58", "#264B35", "#F0F5F2"] },
  { id: "forest",         name: "Forest",                   colors: ["#2E6E4B", "#1A4530", "#E4F0E9"] },

  // Purples — mental health, sexual health, lab vendors
  { id: "mental-health",  name: "Mental health lavender",   colors: ["#7252D6", "#443180", "#F4F1FC"] },
  { id: "plum",           name: "Plum",                     colors: ["#6F4BB8", "#3F2670", "#EFE9F8"] },
  { id: "lab-purple",     name: "Lab purple",               colors: ["#6D28D9", "#411882", "#F3EEFC"] },
  { id: "sexual-health",  name: "Sexual health purple",     colors: ["#7C3AED", "#4A238E", "#F5EFFE"] },

  // Reds & rose — maternity, university hospital, RDi house red
  { id: "maternity",      name: "Maternity rose",           colors: ["#BE3F73", "#722645", "#FAF0F4"] },
  { id: "university",     name: "University claret",        colors: ["#7C1F2E", "#4A131C", "#F5EDEE"] },
  { id: "rdi",            name: "RDi Red",                  colors: ["#D72027", "#1A1A1A", "#F5F5F5"] },

  // Ambers & ochres — children's, pathology, general amber
  { id: "children",       name: "Children's sunshine",      colors: ["#A86308", "#653B05", "#F8F3EB"] },
  { id: "amber",          name: "Amber",                    colors: ["#B45309", "#6B3208", "#FBEEDC"] },
  { id: "pathology",      name: "Pathology ochre",          colors: ["#A16207", "#613B04", "#F7F2EB"] },

  // Neutrals — for clients who don't want brand colour at all
  { id: "slate",          name: "Graphite",                 colors: ["#384550", "#1F2A34", "#E9ECEE"] },
  { id: "warm-grey",      name: "Warm grey",                colors: ["#57534E", "#34322F", "#F2F1F1"] },
  { id: "clean-black",    name: "Clean black",              colors: ["#1A1A1A", "#0A0A0A", "#EDEDED"] },
];
